import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { jobsApi } from '../api/jobs'
import type { JobStatus, JobSummary, JobType, Paged } from '../api/types'
import { BbIcon } from '../components/BbIcon'
import type { BbIconName } from '../components/BbIcon'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatInt } from '../lib/format'

/* Job History, ported from "batchbooks_job_history_synced".

   The prototype's layout is followed: a Job ID / Date-Time / Operation / Entity
   / Status / Actions table with a date-range control and an operation-type
   filter up top. The two extra columns the old list had (company, credits) are
   dropped, and the operation type is shown with the same icon+label the
   prototype uses. `status` is the app's own live filter; the "Last 30 Days"
   picker is the prototype's control but the jobs contract has no date filter
   yet, so it is presentational. */

const OPERATIONS: Record<JobType, { icon: BbIconName; label: string }> = {
  Import: { icon: 'upload', label: 'Import' },
  Export: { icon: 'download', label: 'Export' },
  Delete: { icon: 'trash', label: 'Delete' },
  Modify: { icon: 'edit', label: 'Modify' },
  Backup: { icon: 'save', label: 'Backup' },
}

const TYPE_FILTERS: Array<{ label: string; value: JobType | '' }> = [
  { label: 'All Operations', value: '' },
  { label: 'Import', value: 'Import' },
  { label: 'Export', value: 'Export' },
  { label: 'Delete', value: 'Delete' },
  { label: 'Backup', value: 'Backup' },
]

const STATUS_FILTERS: Array<{ label: string; value: JobStatus | '' }> = [
  { label: 'Any status', value: '' },
  { label: 'Running', value: 'Running' },
  { label: 'Queued', value: 'Pending' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Partial', value: 'PartialSuccess' },
  { label: 'Failed', value: 'Failed' },
  { label: 'Cancelled', value: 'Cancelled' },
]

const PAGE_SIZE = 10

/** "#EXP-8502" — the three-letter type tag plus a short id, matching the prototype. */
function jobTag(job: JobSummary): string {
  const tag = job.type.slice(0, 3).toUpperCase()
  return `#${tag}-${job.id.slice(0, 4)}`
}

/** Status stem + icon, matching the prototype's coloured icon-and-label cells. */
function statusToken(status: JobStatus): { icon: BbIconName; color: string } {
  switch (status) {
    case 'Completed':
      return { icon: 'check-circle', color: 'var(--bb-green)' }
    case 'PartialSuccess':
      return { icon: 'warning', color: 'var(--bb-amber)' }
    case 'Running':
    case 'Pending':
      return { icon: 'sync', color: 'var(--bb-amber)' }
    case 'Cancelled':
    case 'CancellationRequested':
      return { icon: 'x-circle', color: 'var(--bb-on-surface-variant)' }
    case 'Failed':
    default:
      return { icon: 'x-circle', color: 'var(--bb-red)' }
  }
}

/** "Oct 24, 2023" / "14:50:00 UTC" — the two-line Date/Time cell. */
function dateParts(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: '—', time: '' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' }
  return {
    date: d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    time: `${d.toLocaleTimeString('en-US', { hour12: false })} UTC`,
  }
}

export function JobsListPage() {
  const [type, setType] = useState<JobType | ''>('')
  const [status, setStatus] = useState<JobStatus | ''>('')
  const [page, setPage] = useState(1)

  const downloadResult = async (id: string) => {
    try {
      await jobsApi.downloadResult(id)
    } catch (cause: unknown) {
      // A download that silently does nothing is worse than a short-lived note.
      window.alert(cause instanceof ApiError ? cause.message : 'Could not download the export.')
    }
  }

  const jobs = useAsync<Paged<JobSummary>>(
    () =>
      jobsApi.list({
        page,
        pageSize: PAGE_SIZE,
        type: type || undefined,
        status: status || undefined,
      }),
    [page, type, status],
  )

  const items = jobs.data?.items ?? []
  const total = jobs.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const shownFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const shownTo = Math.min(page * PAGE_SIZE, total)

  return (
    <div className="bb bb-page bb-page-wide">
      <div className="bb-page-head">
        <div>
          <h1>Job History</h1>
          <p>Review past bulk operations and their statuses.</p>
        </div>
        <div className="bb-filter-row" style={{ marginBottom: 0 }}>
          <select
            className="bb-select"
            style={{ width: 168 }}
            aria-label="Date range"
            value="last30"
            disabled
          >
            <option value="last30">Last 30 Days</option>
          </select>
          <select
            className="bb-select"
            style={{ width: 168 }}
            aria-label="Operation type"
            value={type}
            onChange={(event) => {
              setType(event.target.value as JobType | '')
              setPage(1)
            }}
          >
            {TYPE_FILTERS.map((option) => (
              <option value={option.value} key={option.label}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="bb-select"
            style={{ width: 150 }}
            aria-label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as JobStatus | '')
              setPage(1)
            }}
          >
            {STATUS_FILTERS.map((option) => (
              <option value={option.value} key={option.label}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bb-activity">
        {jobs.error ? (
          <ErrorState error={jobs.error} onRetry={jobs.reload} />
        ) : jobs.loading && !jobs.data ? (
          <LoadingState label="Loading job history…" />
        ) : items.length === 0 ? (
          <EmptyState
            icon="↑"
            title="No jobs match this filter"
            body="Run an import or export and it will show up here with its result and credit cost."
            action={
              <Link className="bb-btn bb-btn-green" to="/app/import">
                Start an import
              </Link>
            }
          />
        ) : (
          <table className="bb-rtable">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Date/Time</th>
                <th>Operation</th>
                <th>Entity</th>
                <th>Status</th>
                <th className="bb-a-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((job) => {
                const op = OPERATIONS[job.type]
                const token = statusToken(job.status)
                const { date, time } = dateParts(job.createdAt)
                return (
                  <tr className="hov" key={job.id}>
                    <td>
                      <span className="mono fs13">{jobTag(job)}</span>
                    </td>
                    <td className="fs13">
                      {date}
                      <div className="fs12 faint">{time}</div>
                    </td>
                    <td className="fs13">
                      <span className="bb-op">
                        <BbIcon name={op.icon} />
                        {op.label}
                      </span>
                    </td>
                    <td className="fs13">
                      {job.entityType}
                      {job.status === 'Pending' || job.status === 'Running' ? (
                        <div className="fs12 faint">Calculating…</div>
                      ) : job.totalRows > 0 ? (
                        <div className="fs12 faint">{formatInt(job.totalRows)} records</div>
                      ) : null}
                    </td>
                    <td>
                      <span className="bb-status" style={{ color: token.color }}>
                        <BbIcon name={token.icon} />
                        {job.status === 'Completed' && job.errorRows > 0
                          ? `Completed (${formatInt(job.errorRows)} Errs)`
                          : job.status === 'PartialSuccess'
                            ? 'Finished (Warn)'
                            : job.status === 'Pending'
                              ? 'In Progress'
                              : job.status}
                      </span>
                    </td>
                    <td className="bb-a-right">
                      <Link
                        className="bb-alink"
                        to={`/app/jobs/${job.id}`}
                        aria-label={`View ${jobTag(job)}`}
                      >
                        <BbIcon name="eye" />
                      </Link>
                      {job.resultAvailable ? (
                        <button
                          className="bb-alink bb-alink-quiet"
                          type="button"
                          aria-label={`Download ${jobTag(job)}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            void downloadResult(job.id)
                          }}
                        >
                          <BbIcon name="download" />
                        </button>
                      ) : null}
                      {job.type === 'Import' && job.status === 'Completed' ? (
                        <Link
                          className="bb-alink bb-alink-quiet"
                          to={`/app/jobs/${job.id}`}
                          aria-label={`Undo ${jobTag(job)}`}
                        >
                          <BbIcon name="undo" />
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <div className="bb-tablefoot">
          <span className="fs12">
            Showing {formatInt(shownFrom)} to {formatInt(shownTo)} of {formatInt(total)} entries
          </span>
          <span className="bb-pager-c" style={{ marginLeft: 'auto' }}>
            <button
              className="bb-pager-b"
              type="button"
              disabled={page <= 1}
              aria-label="Previous page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &lsaquo;
            </button>
            {Array.from({ length: lastPage }, (_, i) => i + 1).map((n) => (
              <button
                className={n === page ? 'bb-pager-b bb-pager-on' : 'bb-pager-b'}
                type="button"
                key={n}
                aria-current={n === page ? 'page' : undefined}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              className="bb-pager-b"
              type="button"
              disabled={page >= lastPage}
              aria-label="Next page"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              &rsaquo;
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
